import React, { useState } from 'react';
import Navigation from '../components/Navigation';
import SEO from '../components/SEO';

function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'What is a disposable email address?',
      answer: 'A disposable email address is a temporary email that you can use for one-time registrations, verifications, or any situation where you don\'t want to share your real email address. These emails automatically expire after a set time period.'
    },
    {
      question: 'How long do emails last?',
      answer: 'All temporary email addresses and their messages automatically expire and are permanently deleted after 30 minutes. You can also manually delete your inbox at any time.'
    },
    {
      question: 'Is this service free?',
      answer: 'Yes! FreeDisposableEmail.com is completely free to use. No registration, no credit card, no hidden fees.'
    },
    {
      question: 'Do I need to create an account?',
      answer: 'No, you don\'t need to create an account. Simply visit our website, click the button to generate an email, and start using it immediately.'
    },
    {
      question: 'Can I receive attachments?',
      answer: 'Yes, you can receive email attachments. All attachments are stored temporarily and will be deleted along with the email after 30 minutes.'
    },
    {
      question: 'Is my data private and secure?',
      answer: 'Yes, we take privacy seriously. We don\'t require registration, don\'t track users, and all emails are automatically deleted after 30 minutes. We never share your information with third parties.'
    },
    {
      question: 'Can I reply to emails?',
      answer: 'No, disposable email addresses are receive-only. They are designed for one-way communication to protect your privacy.'
    },
    {
      question: 'What happens if I close my browser?',
      answer: 'Your temporary email address remains active for 30 minutes from creation, regardless of whether your browser is open. However, you\'ll need to remember or save the email address to access it again.'
    },
    {
      question: 'Can I use the same email address multiple times?',
      answer: 'Each time you generate an email, you get a unique address. Once an email expires (after 30 minutes), that address cannot be reused.'
    },
    {
      question: 'Is this service legal?',
      answer: 'Yes, using temporary email addresses is completely legal. However, you must use the service responsibly and in accordance with our Terms of Service. Do not use it for illegal activities, spam, or harassment.'
    },
    {
      question: 'Why would I use a disposable email?',
      answer: 'Disposable emails are perfect for: protecting your privacy, avoiding spam, quick sign-ups, one-time verifications, testing services, and any situation where you don\'t want to share your real email address.'
    },
    {
      question: 'How do I delete my inbox?',
      answer: 'You can delete your inbox at any time by clicking the "Delete Inbox" button in your inbox view. This will immediately and permanently delete all emails and the inbox.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="FAQ - FreeDisposableEmail.com | Frequently Asked Questions"
        description="Find answers to common questions about FreeDisposableEmail.com. Learn how to use temporary emails, protect your privacy, and get the most out of our service."
        keywords="FAQ, frequently asked questions, temporary email FAQ, disposable email questions, help, support"
        structuredData={faqStructuredData}
      />
      <Navigation />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h1>
          <p className="text-xl text-gray-600">
            Find answers to common questions about FreeDisposableEmail.com
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                <svg
                  className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${
                    openIndex === index ? 'transform rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openIndex === index && (
                <div className="px-6 py-4 border-t border-gray-200">
                  <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 bg-blue-50 rounded-lg p-6 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Still have questions?</h2>
          <p className="text-gray-700 mb-4">
            Can't find the answer you're looking for? Please feel free to contact us.
          </p>
          <a
            href="/contact"
            className="inline-block bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all"
          >
            Contact Us
          </a>
        </div>
      </div>
    </div>
  );
}

export default FAQ;
